import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiShoppingBag, FiUsers, FiShield } from 'react-icons/fi';
import ProductCard from '../components/product/ProductCard';
import FlashDeals from '../components/deals/FlashDeals';
import ProductRecommendations from '../components/product/ProductRecommendations';
import productService from '../services/product.service';
import { useLanguage } from '../context/LanguageContext';
import heroBanner from '../assets/hero-banner.png';

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, lang } = useLanguage();

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        setLoading(true);
        const res = await productService.getAll({ limit: 8, sort: '-createdAt' });
        console.log('Products API response:', res.data);
        const products = res.data?.data || res.data?.products || res.data || [];
        setFeaturedProducts(Array.isArray(products) ? products : []);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, [lang]);

  return (
    <>
      {/* Hero */}
      <section style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
      }}>
        {/* Background Image */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${heroBanner})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
        }} />

        {/* Gradient Overlay — dark on left for text, transparent on right to show image */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to right, rgba(6, 78, 59, 0.92) 0%, rgba(6, 78, 59, 0.85) 35%, rgba(6, 78, 59, 0.5) 60%, rgba(6, 78, 59, 0.15) 100%)',
        }} />

        {/* Content */}
        <div className="container" style={{ position: 'relative', zIndex: 2, padding: '4rem 0' }}>
          <div className="row">
            <div className="col-lg-7 col-md-8">
              <p style={{
                color: '#EAB308', fontWeight: 700, fontSize: '14px', letterSpacing: '2px',
                textTransform: 'uppercase', marginBottom: '12px',
              }}>
                🌾 {t('empoweringLocalSellers')}
              </p>

              <h1 style={{
                color: '#FFF', fontWeight: 900, fontSize: 'clamp(2.2rem, 5vw, 3.8rem)',
                lineHeight: 1.1, marginBottom: '20px',
              }}>
                {t('heroTitle1')}{' '}
                <span style={{ color: '#EAB308' }}>{t('heroHighlight')}</span>
                <br />{t('heroTitle2')}
              </h1>

              <p style={{
                color: 'rgba(255,255,255,0.85)', fontSize: '1.15rem',
                maxWidth: '480px', lineHeight: 1.7, marginBottom: '32px',
              }}>
                {t('heroSubtitle')}
              </p>

              <div className="d-flex flex-wrap gap-3">
                <Link to="/products" className="btn btn-lg px-4 py-2" style={{
                  background: '#EAB308', color: '#064E3B', fontWeight: 700,
                  border: 'none', borderRadius: '8px', fontSize: '1rem',
                }}>
                  {t('startShopping')} <FiArrowRight className="ms-2" />
                </Link>
                <Link to="/register?role=merchant" className="btn btn-lg px-4 py-2" style={{
                  background: 'transparent', color: '#FFF', fontWeight: 600,
                  border: '2px solid rgba(255,255,255,0.5)', borderRadius: '8px', fontSize: '1rem',
                }}>
                  {t('becomeASeller')}
                </Link>
              </div>

              {/* Trust badges */}
              <div className="d-flex flex-wrap gap-4 mt-4" style={{ opacity: 0.7 }}>
                {[
                  { icon: '🛒', key: 'tenKProducts' },
                  { icon: '🏪', key: 'fiveHundredSellers' },
                  { icon: '⭐', key: 'rating48' },
                ].map((badge, i) => (
                  <span key={i} style={{ color: '#FFF', fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '5px' }}>
                    {badge.icon} {t(badge.key)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding">
        <div className="container">
          <div className="row g-4 text-center">
            <div className="col-md-4">
              <div className="dashboard-card">
                <FiShoppingBag size={40} className="text-primary mb-3" />
                <h5>{t('wideSelection')}</h5>
                <p className="text-muted">{t('wideSelectionDesc')}</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="dashboard-card">
                <FiUsers size={40} className="text-primary mb-3" />
                <h5>{t('trustedSellers')}</h5>
                <p className="text-muted">{t('trustedSellersDesc')}</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="dashboard-card">
                <FiShield size={40} className="text-primary mb-3" />
                <h5>{t('securePayments')}</h5>
                <p className="text-muted">{t('securePaymentsDesc')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Flash Deals */}
      <FlashDeals />

      {/* Featured Products */}
      <section className="section-padding bg-white">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>{t('featuredProducts')}</h2>
            <Link to="/products" className="btn btn-ebw-outline">
              {t('viewAll')} <FiArrowRight className="ms-1" />
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="row g-4">
              {featuredProducts.map((product) => (
                <div key={product._id} className="col-sm-6 col-md-4 col-lg-3">
                  <ProductCard product={product} />
                </div>
              ))}
              {featuredProducts.length === 0 && (
                <div className="col-12 text-center py-5">
                  <p className="text-muted">{t('noProductsYet')}</p>
                  <Link to="/register?role=merchant" className="btn btn-ebw-primary">
                    {t('startSelling')}
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Trending Recommendations */}
      <section className="section-padding" style={{ background: '#F9FAFB' }}>
        <div className="container">
          <ProductRecommendations type="trending" limit={8} />
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-primary text-white text-center">
        <div className="container">
          <h2 className="mb-3">{t('readyToSell')}</h2>
          <p className="mb-4 opacity-75">{t('readyToSellDesc')}</p>
          <Link to="/register?role=merchant" className="btn btn-lg px-5" style={{ background: '#EAB308', color: '#064E3B', fontWeight: 700, border: 'none' }}>
            {t('createYourStore')}
          </Link>
        </div>
      </section>
    </>
  );
};

export default HomePage;
